import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { config } from '../../config/environment';
import UnsavedChangesModal from '../../components/ui/UnsavedChangesModal';

// Icons
import { 
  ArrowLeft, 
  Download, 
  Printer, 
  Package, 
  Trash2,
  CheckCircle, 
  Calendar,
  DollarSign,
  FileText,
  Loader2,
  Save,
  History,
  AlertTriangle,
  Paperclip,
  Upload,
  File,
  X,
  Search,
  Plus
} from 'lucide-react';

import purchaseOrderService from '../../services/purchaseOrderService';

// ============================================================
// MAIN COMPONENT - PO DETAIL/EDIT VIEW
// ============================================================
export default function PurchaseOrderDetail({ poId, onBack, onNavigate, onPOUpdated }) {
  const tenantId = localStorage.getItem('tenant_id') || 'tenant-123';
  
  // FIX #1: Handle back navigation - support both onBack and onNavigate
  const handleBackNavigation = () => {
    if (onNavigate) {
      onNavigate('list');
    } else if (onBack) {
      onBack();
    }
  };
  
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Track original values to show what changed
  const [originalData, setOriginalData] = useState(null);
  
  // Unsaved changes modal state
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  
  // Editable state
  const [editData, setEditData] = useState({
    status: '',
    order_date: '',
    expected_delivery_date: '',
    payment_terms: 'Net 30',
    warehouse: 'Main Warehouse',
    notes: '',
    line_items: [],
    cost_breakdown: {
      intl_freight: 0,
      import_duties: 0,
      inbound_handling: 0,
      domestic_freight: 0
    }
  });
  
  // Receiving state
  const [receiving, setReceiving] = useState({
    items: {},
    invoice_number: '',
    receiving_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [submittingReceive, setSubmittingReceive] = useState(false);
  
  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Add product inline search state (NO MODAL - same as Create PO)
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDropdownRef = useRef(null);

  // ============================================================
  // FIX #3: Warn before leaving with unsaved changes
  // ============================================================
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  // Close product search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch PO details
  useEffect(() => {
    fetchPODetails();
  }, [poId]);

  const fetchPODetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await purchaseOrderService.getPurchaseOrder(poId);
      setPo(response);
      
      // Initialize edit data from PO
      setEditData({
        status: response.status || 'draft',
        order_date: response.order_date?.split('T')[0] || '',
        expected_delivery_date: response.expected_delivery_date?.split('T')[0] || '',
        payment_terms: response.payment_terms || 'Net 30',
        warehouse: response.warehouse || 'Main Warehouse',
        notes: response.notes || '',
        vendor_po_number: response.vendor_po_number || '',
        line_items: response.line_items?.map(item => ({
          ...item,
          quantity: item.quantity || item.quantity_ordered || 0,
          weight: item.weight ?? '',
          weight_unit: item.weight_unit || 'KG',
          unit_price: item.unit_price || item.unit_manufacturing_cost || 0,
          // Handle both old field names (freight_per_unit) and new field names (freight_cost)
          // Use nullish coalescing to preserve 0 values
          freight_cost: item.freight_cost ?? item.freight_per_unit ?? '',
          duties_cost: item.duties_cost ?? item.import_duties_per_unit ?? ''
        })) || [],
        cost_breakdown: {
          intl_freight: response.cost_breakdown?.international_freight || 0,
          import_duties: response.cost_breakdown?.import_duties || 0,
          inbound_handling: response.cost_breakdown?.inbound_handling || 0,
          domestic_freight: response.cost_breakdown?.domestic_freight || 0
        }
      });
      
      // Store original data for change tracking
      setOriginalData({
        status: response.status || 'draft',
        order_date: response.order_date?.split('T')[0] || '',
        expected_delivery_date: response.expected_delivery_date?.split('T')[0] || '',
        payment_terms: response.payment_terms || 'Net 30',
        warehouse: response.warehouse || 'Main Warehouse',
        notes: response.notes || '',
        line_items: response.line_items?.map(item => ({
          sku: item.sku,
          quantity: item.quantity || item.quantity_ordered || 0,
          unit_cost: item.unit_price || item.unit_manufacturing_cost || 0
        })) || [],
        cost_breakdown: {
          intl_freight: response.cost_breakdown?.international_freight || 0,
          import_duties: response.cost_breakdown?.import_duties || 0,
          inbound_handling: response.cost_breakdown?.inbound_handling || 0,
          domestic_freight: response.cost_breakdown?.domestic_freight || 0
        }
      });
      
      // Initialize receiving items
      const receivingItems = {};
      response.line_items?.forEach(item => {
        receivingItems[item.sku] = 0;
      });
      setReceiving(prev => ({ ...prev, items: receivingItems }));
      
      // Fetch attachments
      fetchAttachments();
      
      // Reset hasChanges when data is loaded
      setHasChanges(false);
      
    } catch (err) {
      console.error('Error fetching PO details:', err);
      setError(err.message || 'Failed to load purchase order');
      toast.error('Failed to load purchase order');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch PO attachments
  const fetchAttachments = async () => {
    try {
      const response = await api.get(`/api/purchase-orders/${poId}/attachments`, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      setAttachments(response.attachments || []);
    } catch (err) {
      console.error('Error fetching attachments:', err);
      // Don't show error toast - attachments are optional
    }
  };
  
  // Upload attachment - Supports PDF, images, Excel, Word, CSV
  const handleUploadAttachment = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type - Extended to support more formats
    const allowedTypes = [
      'application/pdf',
      'image/jpeg', 'image/png', 'image/jpg',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/csv'
    ];
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.xlsx', '.xls', '.csv', '.doc', '.docx'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      toast.error('Invalid file type', { 
        description: 'Allowed: PDF, JPG, PNG, Excel (.xlsx, .xls), Word (.doc, .docx), CSV' 
      });
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large', { description: 'Maximum file size is 10MB' });
      return;
    }
    
    // Check max attachments limit
    if (attachments.length >= 20) {
      toast.error('Attachment limit reached', { description: 'Maximum 20 files per PO' });
      return;
    }
    
    setUploadingFile(true);
    try {
      // Use FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      
      // Use config.backendUrl for production compatibility
      const uploadUrl = `${config.backendUrl}/api/purchase-orders/${poId}/attachments`;
      console.log('📤 Uploading to:', uploadUrl);
      
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'X-Tenant-ID': tenantId
        },
        body: formData
      });
      
      // Get response as text first to handle HTML error pages
      const responseText = await response.text();
      
      // Check if response is HTML (error page) instead of JSON
      if (responseText.startsWith('<!') || responseText.startsWith('<html')) {
        console.error('Server returned HTML instead of JSON:', responseText.substring(0, 200));
        throw new Error(`Server error (${response.status}). Please try again.`);
      }
      
      // Try to parse as JSON
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Invalid JSON response:', responseText.substring(0, 200));
        throw new Error('Invalid server response. Please try again.');
      }
      
      if (!response.ok) {
        throw new Error(result.detail || result.error || `Upload failed (${response.status})`);
      }
      
      toast.success('File uploaded successfully');
      
      // Immediately update state with new attachment for instant UI feedback
      if (result.attachment) {
        setAttachments(prev => [...prev, result.attachment]);
      } else {
        // Fallback: re-fetch all attachments
        await fetchAttachments();
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      toast.error('Failed to upload file', { description: err.message });
    } finally {
      setUploadingFile(false);
      e.target.value = ''; // Reset file input
    }
  };
  
  // Delete attachment
  const handleDeleteAttachment = async (fileId, filename) => {
    if (!window.confirm(`Delete "${filename}"?`)) return;
    
    try {
      await api.delete(`/api/purchase-orders/${poId}/attachments/${fileId}`, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      toast.success('Attachment deleted');
      fetchAttachments();
    } catch (err) {
      console.error('Error deleting attachment:', err);
      toast.error('Failed to delete attachment');
    }
  };
  
  // Download attachment
  const handleDownloadAttachment = async (fileId, filename) => {
    try {
      // Use config.backendUrl for production compatibility
      const downloadUrl = `${config.backendUrl}/api/purchase-orders/${poId}/attachments/${fileId}/download`;
      console.log('📥 Downloading from:', downloadUrl);
      
      const response = await fetch(downloadUrl, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      
      if (!response.ok) {
        // Check if response is HTML error page
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
          throw new Error(`Server error (${response.status})`);
        }
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading attachment:', err);
      toast.error('Failed to download attachment');
    }
  };

  // ============================================================
  // FIX #1: Back button with unsaved changes warning (CUSTOM MODAL)
  // ============================================================
  const handleBackClick = () => {
    if (hasChanges) {
      setPendingNavigation('back');
      setShowUnsavedModal(true);
    } else {
      handleBackNavigation();
    }
  };

  // Get list of changes for the modal
  const getChangedFields = () => {
    if (!originalData || !editData) return [];
    
    const changes = [];
    
    // Check simple fields
    const simpleFields = ['status', 'order_date', 'expected_delivery_date', 'payment_terms', 'warehouse', 'notes'];
    simpleFields.forEach(field => {
      const oldVal = originalData[field];
      const newVal = editData[field];
      if (oldVal !== newVal) {
        changes.push({
          field,
          oldValue: oldVal,
          newValue: newVal
        });
      }
    });
    
    // Check cost breakdown fields
    const costFields = ['intl_freight', 'import_duties', 'inbound_handling', 'domestic_freight'];
    costFields.forEach(field => {
      const oldVal = originalData.cost_breakdown?.[field] || 0;
      const newVal = editData.cost_breakdown?.[field] || 0;
      if (oldVal !== newVal) {
        changes.push({
          field: field.replace(/_/g, ' '),
          oldValue: `$${oldVal.toFixed(2)}`,
          newValue: `$${newVal.toFixed(2)}`
        });
      }
    });
    
    // Check line items count
    const oldItemCount = originalData.line_items?.length || 0;
    const newItemCount = editData.line_items?.length || 0;
    if (oldItemCount !== newItemCount) {
      changes.push({
        field: 'Line Items',
        oldValue: `${oldItemCount} items`,
        newValue: `${newItemCount} items`
      });
    }
    
    // Check individual line item changes
    editData.line_items?.forEach((item, idx) => {
      const origItem = originalData.line_items?.[idx];
      if (origItem) {
        if (item.quantity !== origItem.quantity) {
          changes.push({
            field: `${item.sku || 'Item'} Qty`,
            oldValue: origItem.quantity,
            newValue: item.quantity
          });
        }
        if (item.unit_cost !== origItem.unit_cost) {
          changes.push({
            field: `${item.sku || 'Item'} Unit Cost`,
            oldValue: `$${(origItem.unit_cost || 0).toFixed(2)}`,
            newValue: `$${(item.unit_cost || 0).toFixed(2)}`
          });
        }
      }
    });
    
    return changes;
  };

  // Handle modal actions
  const handleDiscardChanges = () => {
    setShowUnsavedModal(false);
    setHasChanges(false);
    if (pendingNavigation === 'back') {
      handleBackNavigation();
    }
    setPendingNavigation(null);
  };

  const handleSaveAndContinue = async () => {
    try {
      await handleSave();
      setShowUnsavedModal(false);
      if (pendingNavigation === 'back') {
        handleBackNavigation();
      }
      setPendingNavigation(null);
    } catch (error) {
      // Save failed, keep modal open
      toast.error('Failed to save changes');
    }
  };

  const handleKeepEditing = () => {
    setShowUnsavedModal(false);
    setPendingNavigation(null);
  };

  // ============================================================
  // FIX #3: Mark changes helper - called on ANY field change
  // ============================================================
  const markChanged = () => {
    setHasChanges(true);
  };

  // Handle field changes
  const handleFieldChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
    markChanged(); // FIX #3: Always mark as changed
  };

  // Handle cost field changes
  const handleCostChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      cost_breakdown: {
        ...prev.cost_breakdown,
        [field]: parseFloat(value) || 0
      }
    }));
    markChanged(); // FIX #3: Always mark as changed
  };

  // Handle line item changes
  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...editData.line_items];
    // Fields that should be numeric
    const numericFields = ['quantity', 'unit_price', 'weight', 'freight_cost', 'duties_cost'];
    
    if (field === 'weight_unit') {
      newLineItems[index] = {
        ...newLineItems[index],
        [field]: value
      };
    } else if (numericFields.includes(field)) {
      // Handle numeric fields - allow empty string for optional fields
      const isOptional = ['weight', 'freight_cost', 'duties_cost'].includes(field);
      newLineItems[index] = {
        ...newLineItems[index],
        [field]: value === '' ? (isOptional ? '' : 0) : (parseFloat(value) || 0)
      };
    } else {
      newLineItems[index] = {
        ...newLineItems[index],
        [field]: value
      };
    }
    
    // Recalculate line total including freight and duties
    const item = newLineItems[index];
    const qty = Number(item.quantity || 0);
    const unitPrice = Number(item.unit_price || 0);
    const freight = Number(item.freight_cost || 0);
    const duties = Number(item.duties_cost || 0);
    newLineItems[index].total = (qty * unitPrice) + freight + duties;
    
    setEditData(prev => ({ ...prev, line_items: newLineItems }));
    markChanged(); // FIX #3: Always mark as changed
  };

  // Helper function to convert weight between KG and LB
  const convertWeight = (weight, fromUnit) => {
    if (!weight || weight === '' || isNaN(weight)) return null;
    const numWeight = parseFloat(weight);
    if (fromUnit === 'KG') {
      return (numWeight * 2.20462).toFixed(2); // KG to LB
    } else {
      return (numWeight / 2.20462).toFixed(2); // LB to KG
    }
  };

  // Delete line item
  const handleDeleteLineItem = (index) => {
    const newLineItems = editData.line_items.filter((_, i) => i !== index);
    setEditData(prev => ({ ...prev, line_items: newLineItems }));
    markChanged(); // FIX #3: Always mark as changed
  };

  // Calculate totals - includes per-line and shared costs
  const calculateTotals = () => {
    // Subtotal from line items (qty * unit price)
    const subtotal = editData.line_items.reduce((sum, item) => 
      sum + (Number(item.quantity || 0) * Number(item.unit_price || 0)), 0
    );
    
    // Per-line freight and duties totals
    const lineFreightTotal = editData.line_items.reduce((sum, item) => 
      sum + Number(item.freight_cost || 0), 0
    );
    const lineDutiesTotal = editData.line_items.reduce((sum, item) => 
      sum + Number(item.duties_cost || 0), 0
    );
    
    // Shared/PO-level costs
    const costs = editData.cost_breakdown;
    const sharedCostsTotal = (costs.intl_freight || 0) + (costs.import_duties || 0) + 
                             (costs.inbound_handling || 0) + (costs.domestic_freight || 0);
    
    // Grand total = subtotal + per-line costs + shared costs
    const total = subtotal + lineFreightTotal + lineDutiesTotal + sharedCostsTotal;
    
    return { 
      subtotal, 
      lineFreightTotal, 
      lineDutiesTotal, 
      sharedCostsTotal, 
      total 
    };
  };

  const totals = calculateTotals();

  // Save changes
  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        status: editData.status,
        order_date: editData.order_date,
        expected_delivery_date: editData.expected_delivery_date,
        payment_terms: editData.payment_terms,
        warehouse: editData.warehouse,
        notes: editData.notes,
        vendor_po_number: editData.vendor_po_number || null,
        line_items: editData.line_items.map(item => {
          // Destructure to remove old field names and avoid saving duplicates
          const { freight_per_unit, import_duties_per_unit, ...cleanItem } = item;
          return {
            ...cleanItem,
            weight: item.weight === '' ? null : item.weight,
            weight_unit: item.weight_unit || 'KG',
            freight_cost: item.freight_cost === '' ? 0 : Number(item.freight_cost || 0),
            duties_cost: item.duties_cost === '' ? 0 : Number(item.duties_cost || 0)
          };
        }),
        cost_breakdown: {
          subtotal: totals.subtotal,
          line_freight_total: totals.lineFreightTotal,
          line_duties_total: totals.lineDutiesTotal,
          international_freight: editData.cost_breakdown.intl_freight,
          import_duties: editData.cost_breakdown.import_duties,
          inbound_handling: editData.cost_breakdown.inbound_handling,
          domestic_freight: editData.cost_breakdown.domestic_freight,
          total: totals.total
        }
      };

      await api.put(`/api/purchase-orders/${poId}`, updateData, {
        headers: { 'X-Tenant-ID': tenantId }
      });

      toast.success('✅ Purchase Order saved successfully!');
      setHasChanges(false); // Reset after successful save
      fetchPODetails(); // Refresh data
      
      if (onPOUpdated) {
        onPOUpdated();
      }
    } catch (err) {
      console.error('Error saving PO:', err);
      toast.error('Failed to save changes', {
        description: err.response?.data?.detail || err.message
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle receiving qty change
  const handleReceivingQtyChange = (sku, value) => {
    const item = editData.line_items.find(i => i.sku === sku);
    const maxReceivable = (item?.quantity || 0) - (item?.quantity_received || 0);
    const qty = Math.min(Math.max(0, parseInt(value) || 0), maxReceivable);
    
    setReceiving(prev => ({
      ...prev,
      items: { ...prev.items, [sku]: qty }
    }));
    // Note: Receiving doesn't trigger hasChanges since it's a separate action
  };

  // ============================================================
  // ADD PRODUCT HANDLERS (INLINE DROPDOWN - same as Create PO)
  // ============================================================
  const handleProductSearch = async (query) => {
    setProductSearch(query);
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowProductDropdown(false);
      return;
    }
    
    setSearchLoading(true);
    setShowProductDropdown(true);  // Show dropdown immediately while loading
    try {
      console.log('[PO Edit] Searching for:', query, 'tenant:', tenantId);
      const response = await api.get('/api/v1/product-master/list', {
        params: {
          tenant_id: tenantId,
          search: query,
          page_size: 10
        },
        headers: { 'X-Tenant-ID': tenantId }
      });
      console.log('[PO Edit] Search response:', response);
      // Handle both axios response (with data property) and direct data
      const data = response.data || response;
      const items = data.items || data.products || [];
      console.log('[PO Edit] Items found:', items.length, items.slice(0, 2));
      setSearchResults(items);
      setShowProductDropdown(true);  // Ensure dropdown stays visible after results
    } catch (error) {
      console.error('[PO Edit] Search error:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddProduct = (product) => {
    // Check if product already exists in line items
    const exists = editData.line_items.some(item => item.sku === product.sku);
    if (exists) {
      toast.warning('Product already in line items');
      return;
    }
    
    const newItem = {
      product_id: product.id,
      sku: product.sku,
      product_name: product.name || product.product_name,
      quantity: 100,
      weight: '',
      weight_unit: 'KG',
      unit_price: product.raw_material_cost || product.landed_cost || 0,
      freight_cost: '',
      duties_cost: '',
      quantity_received: 0
    };
    
    setEditData(prev => ({
      ...prev,
      line_items: [...prev.line_items, newItem]
    }));
    
    // Initialize receiving for new item
    setReceiving(prev => ({
      ...prev,
      items: { ...prev.items, [product.sku]: 0 }
    }));
    
    setHasChanges(true);
    setShowProductDropdown(false);
    setProductSearch('');
    setSearchResults([]);
    toast.success(`Added ${product.sku} to line items`);
  };

  // ============================================================
  // FIX #2: Check if any receiving qty > 0
  // ============================================================
  const hasReceivingQty = Object.values(receiving.items).some(qty => qty > 0);

  // Submit receiving
  const handleConfirmReceiving = async () => {
    const itemsToReceive = Object.entries(receiving.items)
      .filter(([_, qty]) => qty > 0)
      .map(([sku, qty]) => ({ sku, quantity_received: qty }));

    if (itemsToReceive.length === 0) {
      toast.error('Please enter quantity for at least one item');
      return;
    }

    setSubmittingReceive(true);
    try {
      await api.post(`/api/purchase-orders/${poId}/receive`, {
        line_items: itemsToReceive,
        invoice_number: receiving.invoice_number,
        receiving_notes: receiving.notes
      }, {
        headers: { 'X-Tenant-ID': tenantId }
      });

      const totalReceived = itemsToReceive.reduce((sum, i) => sum + i.quantity_received, 0);
      toast.success(`✅ Received ${totalReceived} units successfully!`);
      
      // Reset receiving form and refresh data
      setReceiving(prev => ({
        ...prev,
        items: Object.fromEntries(Object.keys(prev.items).map(k => [k, 0])),
        invoice_number: '',
        notes: ''
      }));
      fetchPODetails();
      
    } catch (err) {
      console.error('Error receiving:', err);
      toast.error('Failed to confirm receiving', {
        description: err.response?.data?.detail || err.message
      });
    } finally {
      setSubmittingReceive(false);
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-500',
      submitted: 'bg-blue-500',
      approved: 'bg-emerald-500',
      partial: 'bg-amber-500',
      received: 'bg-green-600',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !po) {
    return (
      <div className="text-center py-12">
        <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Purchase Order Not Found</h3>
        <p className="text-gray-500 mt-2">{error}</p>
        <Button onClick={handleBackNavigation} variant="outline" className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Purchase Orders
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          {/* FIX #1: Back button now calls handleBackClick with unsaved warning */}
          <Button 
            onClick={handleBackClick} 
            variant="ghost" 
            size="sm" 
            className="mb-2 hover:bg-gray-700"
            data-testid="back-to-pos-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to POs
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{po.po_number}</h1>
          <p className="text-gray-500">{po.supplier_name || po.supplier?.name}</p>
        </div>
        <div className="flex gap-2 items-center">
          {/* FIX #3: Unsaved changes warning badge */}
          {hasChanges && (
            <Badge className="bg-amber-500 text-white flex items-center gap-1 animate-pulse">
              <AlertTriangle className="w-3 h-3" />
              Unsaved Changes
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="w-4 h-4 mr-1" />
            Print
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-1" />
            Export
          </Button>
          {/* FIX #3: Save button enables on ANY change */}
          <Button 
            onClick={handleSave} 
            disabled={!hasChanges || saving}
            className={`${hasChanges ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 cursor-not-allowed'}`}
            data-testid="save-changes-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Status Dropdown */}
      <Card className="bg-[#1a2744]">
        <CardContent className="p-3">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400">STATUS:</span>
            <select
              value={editData.status}
              onChange={(e) => handleFieldChange('status', e.target.value)}
              className={`px-3 py-1.5 rounded text-sm font-medium text-white border-0 cursor-pointer ${getStatusColor(editData.status)}`}
              data-testid="status-dropdown"
            >
              <option value="draft">DRAFT</option>
              <option value="submitted">SUBMITTED</option>
              <option value="approved">APPROVED</option>
              <option value="partial">PARTIAL</option>
              <option value="received">RECEIVED</option>
              <option value="cancelled">CANCELLED</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Purchase Order Details */}
      <Card className="bg-[#1a2744]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 text-white">
            <FileText className="w-4 h-4 text-blue-400" />
            PURCHASE ORDER DETAILS
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <label className="text-[10px] text-gray-400 uppercase">PO Number</label>
              <p className="text-sm font-mono text-white">{po.po_number}</p>
            </div>
            <div>
              <label className="text-[10px] text-gray-400 uppercase">Vendor PO #</label>
              <Input
                type="text"
                value={editData.vendor_po_number || ''}
                onChange={(e) => handleFieldChange('vendor_po_number', e.target.value)}
                placeholder="Supplier ref..."
                className="h-7 text-xs bg-[#2a3548] border border-gray-500 text-white focus:border-blue-500"
                data-testid="vendor-po-number-input"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 uppercase">PO Date</label>
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3 text-gray-500" />
                <Input
                  type="date"
                  value={editData.order_date}
                  onChange={(e) => handleFieldChange('order_date', e.target.value)}
                  className="h-7 text-xs bg-[#2a3548] border border-gray-500 text-white focus:border-blue-500"
                  data-testid="po-date-input"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-gray-400 uppercase">Expected Delivery</label>
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3 text-gray-500" />
                <Input
                  type="date"
                  value={editData.expected_delivery_date}
                  onChange={(e) => handleFieldChange('expected_delivery_date', e.target.value)}
                  className="h-7 text-xs bg-[#2a3548] border border-gray-500 text-white focus:border-blue-500"
                  data-testid="expected-delivery-input"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-gray-400 uppercase">Payment Terms</label>
              <select
                value={editData.payment_terms}
                onChange={(e) => handleFieldChange('payment_terms', e.target.value)}
                className="w-full h-7 text-xs bg-[#2a3548] border border-gray-500 rounded text-white px-2 focus:border-blue-500 focus:outline-none"
                data-testid="payment-terms-dropdown"
              >
                <option value="Net 15">Net 15</option>
                <option value="Net 30">Net 30</option>
                <option value="Net 45">Net 45</option>
                <option value="Net 60">Net 60</option>
                <option value="Due on Receipt">Due on Receipt</option>
                <option value="Prepaid">Prepaid</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card className="bg-[#1a2744]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 text-white">
            <Package className="w-4 h-4 text-green-400" />
            LINE ITEMS ({editData.line_items.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 space-y-3">
          {/* INLINE SEARCH DROPDOWN - Same as Create PO */}
          <div className="relative" ref={searchDropdownRef}>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by SKU or product name to add..."
                  value={productSearch}
                  onChange={(e) => handleProductSearch(e.target.value)}
                  onFocus={() => productSearch.length >= 2 && setShowProductDropdown(true)}
                  className="pl-10 h-10 text-sm bg-[#0f1729] border-gray-600 text-white placeholder-gray-500"
                  data-testid="inline-product-search"
                />
                {searchLoading && (
                  <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-blue-400" />
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowProductDropdown(!showProductDropdown)}
                className="h-10 px-3 border-green-600 text-green-400 hover:bg-green-600/20"
                title="Toggle product list"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Dropdown Results */}
            {showProductDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-[#1a2744] border border-gray-600 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {searchLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
                    <span className="ml-2 text-gray-400 text-sm">Searching...</span>
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map(product => (
                    <div
                      key={product.id || product.sku}
                      onClick={() => handleAddProduct(product)}
                      className="p-3 hover:bg-[#0f1729] cursor-pointer transition-colors border-b border-gray-700 last:border-0"
                      data-testid={`product-dropdown-${product.sku}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="font-mono text-sm text-blue-400">{product.sku}</span>
                          <p className="text-sm text-gray-300 line-clamp-1 mt-0.5">
                            {product.name || product.product_name}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-gray-500">Cost</span>
                          <p className="font-mono text-sm text-emerald-400">
                            ${(Number(product.landed_cost) || Number(product.raw_material_cost) || 0).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : productSearch.length >= 2 ? (
                  <div className="text-center py-6 text-gray-400">
                    <Package className="w-6 h-6 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No products found for "{productSearch}"</p>
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-400">
                    <Search className="w-6 h-6 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Type at least 2 characters to search</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Line Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0f1729]">
                <tr>
                  <th className="w-[100px] px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase">SKU</th>
                  <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase">Product</th>
                  <th className="w-[70px] px-2 py-2 text-center text-[10px] font-medium text-gray-400 uppercase">Qty</th>
                  <th className="w-[80px] px-2 py-2 text-right text-[10px] font-medium text-gray-400 uppercase">Unit $</th>
                  <th className="w-[75px] px-2 py-2 text-right text-[10px] font-medium text-amber-400 uppercase">Freight</th>
                  <th className="w-[75px] px-2 py-2 text-right text-[10px] font-medium text-amber-400 uppercase">Duties</th>
                  <th className="w-[90px] px-2 py-2 text-right text-[10px] font-medium text-gray-400 uppercase">Total</th>
                  <th className="w-[40px] px-2 py-2 text-center text-[10px] font-medium text-gray-400 uppercase"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {editData.line_items.map((item, index) => {
                  const lineFreight = Number(item.freight_cost || 0);
                  const lineDuties = Number(item.duties_cost || 0);
                  const unitCost = Number(item.unit_price || 0);
                  const qty = Number(item.quantity || 0);
                  const lineTotal = (qty * unitCost) + lineFreight + lineDuties;
                  
                  return (
                    <tr key={item.sku || index} className="hover:bg-gray-800/30">
                      <td className="px-3 py-2">
                        <span className="font-mono text-xs text-white">{item.sku}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-xs text-gray-300 line-clamp-1">{item.product_name}</span>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                          style={{ backgroundColor: '#3d4f6f', color: '#ffffff', border: '1px solid #6b7999', width: '80px' }}
                          className="h-8 text-sm text-center mx-auto"
                          data-testid={`qty-input-${item.sku}`}
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)}
                          style={{ backgroundColor: '#3d4f6f', color: '#ffffff', border: '1px solid #6b7999', width: '90px' }}
                          className="h-8 text-sm text-right ml-auto"
                          data-testid={`price-input-${item.sku}`}
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.freight_cost || ''}
                          onChange={(e) => handleLineItemChange(index, 'freight_cost', e.target.value)}
                          placeholder="0"
                          style={{ backgroundColor: '#3d4f6f', color: '#fcd34d', border: '1px solid #d97706', width: '80px' }}
                          className="h-8 text-sm text-right ml-auto"
                          data-testid={`freight-input-${item.sku}`}
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.duties_cost || ''}
                          onChange={(e) => handleLineItemChange(index, 'duties_cost', e.target.value)}
                          placeholder="0"
                          style={{ backgroundColor: '#3d4f6f', color: '#fcd34d', border: '1px solid #d97706', width: '80px' }}
                          className="h-8 text-sm text-right ml-auto"
                          data-testid={`duties-input-${item.sku}`}
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <span className="font-mono text-xs font-semibold text-emerald-400">
                          ${lineTotal.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <button
                          onClick={() => handleDeleteLineItem(index)}
                          className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded"
                          title="Remove item"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {editData.line_items.length === 0 && (
                  <tr>
                    <td colSpan="8" className="px-3 py-8 text-center text-gray-500 text-sm">
                      No line items. Click &quot;+ Add Line Item&quot; to add products.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Costs Section */}
      <Card className="bg-[#1a2744]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 text-white">
            <DollarSign className="w-4 h-4 text-amber-400" />
            COSTS SUMMARY
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* FROM LINE ITEMS Section */}
          <div className="p-3 bg-[#0f1729] rounded-lg border border-gray-700">
            <p className="text-[10px] text-blue-400 uppercase font-semibold mb-3">FROM LINE ITEMS</p>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-400">Subtotal (Qty × Unit $):</span>
                <span className="text-sm font-mono text-white">${totals.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-400">Line Freight Total:</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-mono text-amber-300">${totals.lineFreightTotal.toFixed(2)}</span>
                  {totals.subtotal > 0 && (
                    <span className="text-[10px] text-gray-500">({((totals.lineFreightTotal / totals.subtotal) * 100).toFixed(1)}% avg)</span>
                  )}
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-400">Line Duties Total:</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-mono text-amber-300">${totals.lineDutiesTotal.toFixed(2)}</span>
                  {totals.subtotal > 0 && (
                    <span className="text-[10px] text-gray-500">({((totals.lineDutiesTotal / totals.subtotal) * 100).toFixed(1)}% avg)</span>
                  )}
                </div>
              </div>
              <div className="border-t border-gray-700 pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-300">Line Items Total:</span>
                  <span className="text-sm font-mono font-bold text-white">${(totals.subtotal + totals.lineFreightTotal + totals.lineDutiesTotal).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* SHARED PO COSTS Section */}
          <div className="p-3 bg-[#0f1729] rounded-lg border border-gray-700">
            <p className="text-[10px] text-purple-400 uppercase font-semibold mb-3">SHARED PO-LEVEL COSTS</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-[10px] text-gray-400 uppercase block mb-1">Intl Freight</label>
                <div className="flex items-center">
                  <span className="text-gray-500 mr-1">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editData.cost_breakdown.intl_freight}
                    onChange={(e) => handleCostChange('intl_freight', e.target.value)}
                    style={{ backgroundColor: '#3d4f6f', color: '#ffffff', border: '1px solid #6b7999', width: '100px' }}
                    className="h-8 text-sm"
                    data-testid="intl-freight-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] text-gray-400 uppercase block mb-1">Import Duties</label>
                <div className="flex items-center">
                  <span className="text-gray-500 mr-1">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editData.cost_breakdown.import_duties}
                    onChange={(e) => handleCostChange('import_duties', e.target.value)}
                    style={{ backgroundColor: '#3d4f6f', color: '#ffffff', border: '1px solid #6b7999', width: '100px' }}
                    className="h-8 text-sm"
                    data-testid="import-duties-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] text-gray-400 uppercase block mb-1">Inbound Handling</label>
                <div className="flex items-center">
                  <span className="text-gray-500 mr-1">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editData.cost_breakdown.inbound_handling}
                    onChange={(e) => handleCostChange('inbound_handling', e.target.value)}
                    style={{ backgroundColor: '#3d4f6f', color: '#ffffff', border: '1px solid #6b7999', width: '100px' }}
                    className="h-8 text-sm"
                    data-testid="inbound-handling-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] text-gray-400 uppercase block mb-1">Domestic Freight</label>
                <div className="flex items-center">
                  <span className="text-gray-500 mr-1">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editData.cost_breakdown.domestic_freight}
                    onChange={(e) => handleCostChange('domestic_freight', e.target.value)}
                    style={{ backgroundColor: '#3d4f6f', color: '#ffffff', border: '1px solid #6b7999', width: '100px' }}
                    className="h-8 text-sm"
                    data-testid="domestic-freight-input"
                  />
                </div>
              </div>
            </div>
            <div className="border-t border-gray-700 pt-2 mt-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-400">Shared Costs Total:</span>
                <span className="text-sm font-mono text-white">${totals.sharedCostsTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
          
          {/* GRAND TOTAL */}
          <div className="p-4 bg-emerald-900/30 rounded-lg border border-emerald-700/50">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold text-emerald-400">GRAND TOTAL:</span>
              <span className="text-2xl font-mono font-bold text-emerald-400">${totals.total.toFixed(2)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Receiving Section */}
      <Card className="bg-[#1a2744]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 text-white">
            <Package className="w-4 h-4 text-purple-400" />
            RECEIVING
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto mb-4">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-700">
                  <TableHead className="text-gray-400 text-[10px]">SKU</TableHead>
                  <TableHead className="text-gray-400 text-[10px]">Product</TableHead>
                  <TableHead className="text-gray-400 text-[10px] text-center">Ordered</TableHead>
                  <TableHead className="text-gray-400 text-[10px] text-center">Received</TableHead>
                  <TableHead className="text-gray-400 text-[10px] text-center">Remaining</TableHead>
                  <TableHead className="text-purple-400 text-[10px] text-center">Receive Now</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {editData.line_items.map((item) => {
                  const ordered = item.quantity || 0;
                  const received = item.quantity_received || 0;
                  const remaining = ordered - received;
                  const isComplete = remaining === 0;
                  
                  return (
                    <TableRow key={item.sku} className="border-gray-700">
                      <TableCell className="font-mono text-sm text-white">{item.sku}</TableCell>
                      <TableCell className="text-sm text-gray-300 max-w-[150px] truncate">
                        {item.product_name}
                      </TableCell>
                      <TableCell className="text-center text-sm text-white">{ordered}</TableCell>
                      <TableCell className="text-center text-sm">
                        {received > 0 ? (
                          <span className="text-emerald-400 font-medium">{received}</span>
                        ) : (
                          <span className="text-gray-500">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {isComplete ? (
                          <Badge className="bg-emerald-600 text-white text-[10px]">Complete</Badge>
                        ) : (
                          <span className="text-amber-400 font-medium">{remaining}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {isComplete ? (
                          <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" />
                        ) : (
                          <Input
                            type="number"
                            min="0"
                            max={remaining}
                            value={receiving.items[item.sku] || ''}
                            onChange={(e) => handleReceivingQtyChange(item.sku, e.target.value)}
                            placeholder="0"
                            className="w-16 h-7 text-xs text-center bg-[#2a3548] border border-gray-500 text-white mx-auto focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                            data-testid={`receive-now-${item.sku}`}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Receiving Form */}
          <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-[#0f1729] rounded-lg">
            <div>
              <label className="text-[10px] text-gray-400 uppercase block mb-1">Receiving Date</label>
              <Input
                type="date"
                value={receiving.receiving_date}
                onChange={(e) => setReceiving(prev => ({ ...prev, receiving_date: e.target.value }))}
                className="h-7 text-xs bg-[#1a2744] border-gray-700 text-white"
                data-testid="receiving-date-input"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 uppercase block mb-1">Invoice #</label>
              <Input
                type="text"
                value={receiving.invoice_number}
                onChange={(e) => setReceiving(prev => ({ ...prev, invoice_number: e.target.value }))}
                placeholder="Enter invoice number"
                className="h-7 text-xs bg-[#1a2744] border-gray-700 text-white"
                data-testid="invoice-number-input"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 uppercase block mb-1">Notes</label>
              <Input
                type="text"
                value={receiving.notes}
                onChange={(e) => setReceiving(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Receiving notes..."
                className="h-7 text-xs bg-[#1a2744] border-gray-700 text-white"
                data-testid="receiving-notes-input"
              />
            </div>
          </div>

          <div className="flex justify-end">
            {/* FIX #2: Button enables when ANY receiving qty > 0 */}
            <Button
              onClick={handleConfirmReceiving}
              disabled={submittingReceive || !hasReceivingQty}
              className={`${hasReceivingQty ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-600 cursor-not-allowed'}`}
              data-testid="confirm-receiving-btn"
            >
              {submittingReceive ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Confirm Receiving
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Receiving History */}
      {po.receiving_history && po.receiving_history.length > 0 && (
        <Card className="bg-[#1a2744]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-white">
              <History className="w-4 h-4 text-cyan-400" />
              RECEIVING HISTORY
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-gray-700">
                  <TableHead className="text-gray-400 text-[10px]">Date</TableHead>
                  <TableHead className="text-gray-400 text-[10px]">Items</TableHead>
                  <TableHead className="text-gray-400 text-[10px]">Invoice #</TableHead>
                  <TableHead className="text-gray-400 text-[10px]">By</TableHead>
                  <TableHead className="text-gray-400 text-[10px]">Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {po.receiving_history.map((entry, index) => (
                  <TableRow key={index} className="border-gray-700">
                    <TableCell className="text-sm text-white">
                      {new Date(entry.received_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-sm text-gray-300">
                      {entry.items_received?.map(i => `${i.sku}: ${i.qty}`).join(', ')}
                    </TableCell>
                    <TableCell className="text-sm text-gray-300">{entry.invoice_number || '-'}</TableCell>
                    <TableCell className="text-sm text-gray-300">{entry.received_by}</TableCell>
                    <TableCell className="text-sm text-gray-400">{entry.notes || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Attachments Section */}
      <Card className="bg-[#1a2744]">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <Paperclip className="w-4 h-4 text-indigo-400" />
              ATTACHMENTS
              {attachments.length > 0 && (
                <Badge className="bg-indigo-600 text-white text-[10px]">{attachments.length}/20</Badge>
              )}
              <span className="text-[9px] text-gray-500 italic">(auto-saved)</span>
            </div>
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.doc,.docx"
                className="hidden"
                onChange={handleUploadAttachment}
                disabled={uploadingFile || attachments.length >= 20}
                data-testid="file-upload-input"
              />
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-indigo-500 text-indigo-400 hover:bg-indigo-900/30"
                disabled={uploadingFile || attachments.length >= 20}
                asChild
              >
                <span>
                  {uploadingFile ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <Upload className="w-3 h-3 mr-1" />
                  )}
                  Upload File
                </span>
              </Button>
            </label>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {attachments.length > 0 ? (
            <div className="space-y-2">
              {attachments.map((att) => (
                <div
                  key={att.file_id}
                  className="flex items-center justify-between p-2 bg-[#0f1729] rounded-lg group"
                  data-testid={`attachment-${att.file_id}`}
                >
                  <div className="flex items-center gap-2">
                    <File className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-white">{att.filename}</p>
                      <p className="text-[10px] text-gray-500">
                        {att.content_type || att.file_type} • Uploaded {new Date(att.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30"
                      onClick={() => handleDownloadAttachment(att.file_id, att.filename)}
                      title="Download"
                      data-testid={`download-${att.file_id}`}
                    >
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                      onClick={() => handleDeleteAttachment(att.file_id, att.filename)}
                      title="Delete"
                      data-testid={`delete-${att.file_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <Paperclip className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-gray-400 text-xs">No attachments yet</p>
              <p className="text-gray-500 text-[10px] mt-1">
                Upload invoices, receipts, or documents (PDF, Images, Excel, Word, CSV)
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t border-gray-700">
        <Button variant="outline" onClick={handleBackClick}>
          Cancel
        </Button>
        {/* FIX #3: Save button enables on ANY change */}
        <Button 
          onClick={handleSave} 
          disabled={!hasChanges || saving}
          className={`${hasChanges ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 cursor-not-allowed'}`}
          data-testid="save-changes-footer-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onClose={handleKeepEditing}
        onDiscard={handleDiscardChanges}
        onSave={handleSaveAndContinue}
        changes={getChangedFields()}
        saving={saving}
      />
    </div>
  );
}
